import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Table,
  Modal,
  Badge,
  Tabs,
  Tab,
} from 'react-bootstrap';
import {
  useGetWebmonSettings,
  usePatchWebmonSettings,
  useGetWebmonSites,
  usePostWebmonSites,
  usePutWebmonSitesSiteId,
  useDeleteWebmonSitesSiteId,
  usePostWebmonSitesSiteIdChecks,
  usePutWebmonSitesSiteIdChecksCheckId,
  useDeleteWebmonSitesSiteIdChecksCheckId,
  usePostWebmonTryCheck,
  usePostWebmonCheck,
  usePostWebmonTestAlert,
  getGetWebmonSitesQueryKey,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import {
  WebMonSettings,
  MonitoredSite,
  SiteCheck,
  SiteCheckTryResult,
  MonitorTestStage,
  PostWebmonCheck200,
  JsonPatchOperation,
} from '../../api/generated/model';

// Website uptime monitoring.
//
// A status code alone is not enough: nginx's default page, an error page
// served with 200, and a deploy that lost the image service all pass "the
// homepage returned 200" while the site is visibly broken. So each site owns an
// ordered list of check rules, and any one of them failing takes the site down.

const empty: WebMonSettings = {
  enabled: false,
  heartbeat_url: '',
  slack_webhook_url: '',
  alert_recipients: '',
  from_address: '',
  from_name: '',
  user_agent: '',
  target_policy: 'public',
  interval_seconds: 3600,
  probe_timeout_ms: 10000,
  attempts_per_run: 3,
  retry_delay_seconds: 30,
  realert_hours: 24,
};

const runSummary = (res?: PostWebmonCheck200): string => {
  const n = res?.results?.length ?? 0;
  const alerted = res?.alerted ?? 0;
  const recovered = res?.recovered ?? 0;

  const parts = [`Checked ${n} site${n === 1 ? '' : 's'}.`];
  if (alerted > 0) parts.push(`${alerted} alert${alerted === 1 ? '' : 's'} sent.`);
  if (recovered > 0) parts.push(`${recovered} recovery notice${recovered === 1 ? '' : 's'} sent.`);
  if (alerted === 0 && recovered === 0) parts.push('Nothing needed alerting.');
  return parts.join(' ');
};

// A site that has never been probed is not healthy, it is unasked. The state
// column is the empty string until the first run, so checking it alone would
// render a brand new site as passing.
const siteBadge = (s: MonitoredSite): { variant: string; label: string } => {
  // Said before any run, not after one: a site with nothing to check can never
  // be found healthy, and "Never checked" would read as merely pending.
  if ((s.checks?.length ?? 0) === 0) return { variant: 'warning', label: 'No checks' };
  if (!s.last_checked_at) return { variant: 'secondary', label: 'Never checked' };
  switch (s.last_state) {
    case 'up':
      return {
        variant: 'success',
        label: `Up (${(s.total_checks ?? 0) - (s.failing_checks ?? 0)}/${s.total_checks ?? 0})`,
      };
    case 'down':
      return { variant: 'danger', label: `Down (${s.failing_checks ?? 0}/${s.total_checks ?? 0} failing)` };
    case 'error':
      return { variant: 'danger', label: 'Unreachable' };
    default:
      return { variant: 'secondary', label: 'No checks' };
  }
};

const checkBadge = (c: SiteCheck): { variant: string; label: string } => {
  if (!c.last_checked_at) return { variant: 'secondary', label: 'Never run' };
  if (c.last_ok) return { variant: 'success', label: `ok ${c.last_status_code ?? ''}`.trim() };
  return { variant: 'danger', label: `fail ${c.last_status_code ?? ''}`.trim() };
};

const formatWhen = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

// One wording for each policy, used by both the default select and the
// per-site override, so "Inherit — X" names X exactly as the other control
// does. Two spellings of the same value read as two different settings.
const TARGET_POLICY_LABELS: Record<string, string> = {
  public: 'Public internet only',
  loopback: 'Also this machine (localhost)',
  internal: 'Also this network (LAN)',
};

const targetPolicyLabel = (p?: string | null): string =>
  TARGET_POLICY_LABELS[p ?? 'public'] ?? TARGET_POLICY_LABELS.public;

// One line describing what a rule asserts, so the table is readable without
// opening every row.
const describeCheck = (c: SiteCheck): string => {
  if (c.label) return c.label;
  const parts = [`${c.method ?? 'GET'} ${c.url}`];
  if (c.expect_status) parts.push(`→ ${c.expect_status}`);
  if (c.header_op === 'absent') parts.push(`, no ${c.expect_header}`);
  if (c.header_op === 'equals') parts.push(`, ${c.expect_header} = "${c.expect_header_value}"`);
  if (c.header_op === 'prefix') parts.push(`, ${c.expect_header} starts "${c.expect_header_value}"`);
  if (c.expect_body_contains) parts.push(`, body contains "${c.expect_body_contains}"`);
  return parts.join('');
};

// A check's type. Picking one fills the fields in; every field stays editable
// afterwards, so the type is a starting point rather than a mode.
//
// `matches` lets an existing check be named in the list without storing the
// type, which would be a second source of truth for what the fields already
// say.
type Preset = {
  name: string;
  describe: string;
  apply: (base: string) => Partial<SiteCheck>;
  matches?: (c: SiteCheck) => boolean;
};

const PRESETS: Preset[] = [
  {
    name: 'Contact form reaches the API',
    describe:
      'Posts a reserved payload the submissions service recognises, so a broken proxy is caught. Creates no submission and sends no email.',
    apply: () => ({
      method: 'POST',
      url: '/api/contact',
      expect_status: '200',
      follow_redirects: false,
      header_op: '',
      request_body: '{"formId":"__probe__","fields":{}}',
      request_content_type: 'application/json',
      expect_body_contains: '"probe":"web-forms"',
    }),
    matches: (c) => c.method === 'POST' && (c.request_body ?? '').includes('__probe__'),
  },
  {
    name: 'Redirects to',
    describe:
      'Following the redirect would return 200 from the target and hide it, so this one does not follow.',
    apply: () => ({
      method: 'GET',
      url: '/',
      expect_status: '301',
      follow_redirects: false,
      header_op: 'equals',
      expect_header: 'Location',
      expect_header_value: '',
    }),
    matches: (c) => c.follow_redirects === false && c.expect_header === 'Location',
  },
  {
    name: 'Image renders',
    describe:
      'Pages serve from disk while images come from a separate service, so a page can load while every image fails. Point this at a real rendition, not a static file.',
    apply: () => ({
      method: 'GET',
      url: '/',
      expect_status: '200',
      follow_redirects: true,
      header_op: 'prefix',
      expect_header: 'Content-Type',
      expect_header_value: 'image/',
    }),
    matches: (c) => c.header_op === 'prefix' && (c.expect_header_value ?? '').startsWith('image/'),
  },
  {
    name: 'Header must be absent',
    describe: 'Production shipping noindex is a silent outage no status code shows.',
    apply: () => ({
      method: 'GET',
      url: '/',
      expect_status: '200',
      follow_redirects: true,
      header_op: 'absent',
      expect_header: 'X-Robots-Tag',
      expect_header_value: '',
    }),
    matches: (c) => c.header_op === 'absent',
  },
  {
    name: 'Header must equal',
    describe: 'Staging should be telling crawlers to stay away.',
    apply: () => ({
      method: 'GET',
      url: '/',
      expect_status: '200',
      follow_redirects: true,
      header_op: 'equals',
      expect_header: 'X-Robots-Tag',
      expect_header_value: 'noindex, nofollow',
    }),
    matches: (c) => c.header_op === 'equals',
  },
  {
    name: 'Page contains text',
    describe: 'Catches an error page served with 200, or a deploy that shipped the wrong site.',
    apply: () => ({
      method: 'GET',
      url: '/',
      expect_status: '200',
      follow_redirects: true,
      header_op: '',
      expect_body_contains: '',
    }),
    matches: (c) => !!c.expect_body_contains,
  },
  {
    name: 'Page loads',
    describe: 'The page returns the status you expect, and nothing more is asserted.',
    apply: () => ({ method: 'GET', url: '/', expect_status: '200', follow_redirects: true, header_op: '' }),
  },
];

// Order matters: the first match wins, so the specific types are listed before
// the general ones and "Page loads" is the fallback.
const checkTypeOf = (c: SiteCheck): Preset =>
  PRESETS.find((p) => p.matches?.(c)) ?? PRESETS[PRESETS.length - 1];

const WebMonTab: React.FC = () => {
  const { data, isLoading, refetch } = useGetWebmonSettings();
  const patchMutation = usePatchWebmonSettings();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<WebMonSettings>(empty);
  const [original, setOriginal] = useState<WebMonSettings>(empty);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ variant: string; text: string } | null>(null);
  const [actionMsg, setActionMsg] = useState<{ variant: string; text: string } | null>(null);
  const [testStages, setTestStages] = useState<MonitorTestStage[] | null>(null);
  const [running, setRunning] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm(data);
      setOriginal(data);
      setLoaded(true);
    }
  }, [data]);

  const hasChanges = JSON.stringify(form) !== JSON.stringify(original);

  const set = (k: keyof WebMonSettings, v: any) => {
    setForm((p) => ({ ...p, [k]: v }));
    setMsg((m) => (m?.variant === 'success' ? null : m));
  };

  const status = useMemo(() => {
    if (msg?.variant === 'danger') return { text: msg.text, className: 'text-danger', dismissable: true };
    if (hasChanges) return { text: 'Unsaved changes.', className: 'text-muted', dismissable: false };
    if (msg) return { text: msg.text, className: 'text-success', dismissable: false };
    return null;
  }, [msg, hasChanges]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const patches: JsonPatchOperation[] = (Object.keys(form) as (keyof WebMonSettings)[]).map((k) => ({
        op: 'add' as const,
        path: `/${k}`,
        value: form[k],
      }));
      await patchMutation.mutateAsync({ data: patches });
      await refetch();
      setMsg({ variant: 'success', text: 'Settings saved.' });
    } catch (err: any) {
      setMsg({
        variant: 'danger',
        text: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save settings',
      });
    } finally {
      setSaving(false);
    }
  };

  const checkMutation = usePostWebmonCheck();
  const testMutation = usePostWebmonTestAlert();

  const runCheck = async () => {
    setRunning('check');
    setActionMsg(null);
    setTestStages(null);
    try {
      const res = await checkMutation.mutateAsync({ data: {} });
      await queryClient.invalidateQueries({ queryKey: getGetWebmonSitesQueryKey() });
      setActionMsg({ variant: 'success', text: runSummary(res) });
    } catch (err: any) {
      setActionMsg({ variant: 'danger', text: err?.response?.data?.message || 'Failed to run the check' });
    } finally {
      setRunning(null);
    }
  };

  const sendTestAlert = async () => {
    setRunning('test');
    setActionMsg(null);
    try {
      const res: any = await testMutation.mutateAsync();
      setTestStages(res ?? []);
    } catch (err: any) {
      setActionMsg({
        variant: 'danger',
        text: err?.response?.data?.message || 'Failed to send the test alert',
      });
    } finally {
      setRunning(null);
    }
  };

  const textField = (label: string, k: keyof WebMonSettings, help?: string) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control type="text" value={String(form[k] ?? '')} onChange={(e) => set(k, e.target.value)} />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  const numField = (label: string, k: keyof WebMonSettings, help?: string) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type="number"
        value={Number(form[k] ?? 0)}
        onChange={(e) => set(k, parseInt(e.target.value || '0', 10))}
      />
      {help && <Form.Text muted>{help}</Form.Text>}
    </Form.Group>
  );

  if (isLoading || !loaded) {
    return (
      <>
        <Card className="mb-3">
          <Card.Header>
            <span>
              <i className="bi bi-globe2 me-2" />
              Website monitoring
            </span>
          </Card.Header>
          <Card.Body>
            <div className="text-center py-4 text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading settings…
            </div>
          </Card.Body>
        </Card>
        <MonitoredSitesCard settings={empty} />
      </>
    );
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Header>
          <span>
            <i className="bi bi-globe2 me-2" />
            Website monitoring
          </span>
        </Card.Header>
        <Card.Body>
          <Form.Check
            type="switch"
            id="webmon-enabled"
            label="Check sites on a schedule"
            checked={!!form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
            className="mb-1"
          />
          {form.enabled && !String(form.heartbeat_url ?? '').trim() && (
            <Alert variant="warning" className="py-2 mb-2">
              No dead-man&rsquo;s switch configured. Checks will still run and alert — but this monitor runs
              inside the dashboard, so if the dashboard stops, the checks stop with it and nothing will say
              so. Set a heartbeat URL below when you have one.
            </Alert>
          )}
          <Form.Text muted className="d-block mb-3">
            Each site is checked on its own interval. The checks run in this process rather than from a timer,
            which is what lets a site be checked every few minutes or once a day without everything else
            having to match.
          </Form.Text>

          <hr />
          <h6 className="text-muted">Dead-man&rsquo;s switch</h6>
          {textField(
            'Heartbeat URL',
            'heartbeat_url',
            'Pinged when a run starts and again when it finishes, including when a site is down and the alert was sent — it means "the check ran", not "the site is healthy". The provider alerts when the pings stop. Its period must be no longer than your shortest site interval.',
          )}

          <hr />
          <h6 className="text-muted">Defaults</h6>
          <Form.Text muted className="d-block mb-3">
            Every value below is a default. A site can override any of them, and clearing an override on a
            site returns it to what is set here — so rotating a webhook is one edit rather than one per site.
          </Form.Text>
          <Row>
            <Col md={6}>
              {textField('Slack webhook URL', 'slack_webhook_url', 'Leave blank to skip Slack.')}
            </Col>
            <Col md={6}>
              {textField('Alert recipients', 'alert_recipients', 'Comma-separated email addresses.')}
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {textField(
                'From address',
                'from_address',
                'Must be a domain we control, or the relay will reject it.',
              )}
            </Col>
            <Col md={6}>{textField('From name', 'from_name')}</Col>
          </Row>
          <Row>
            <Col md={6}>
              {textField(
                'User agent',
                'user_agent',
                'Sent on every probe. Some firewalls block the default Go agent outright, which would make a healthy site read as down.',
              )}
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Probe reach</Form.Label>
                <Form.Select
                  value={String(form.target_policy ?? 'public')}
                  onChange={(e) => set('target_policy', e.target.value)}
                >
                  {Object.entries(TARGET_POLICY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>
                  Public only is the right answer unless you are watching something that is not on the public
                  internet. How far inside the network a probe may reach. Widen it only if you are watching
                  something that is not on the public internet — the prober runs inside the dashboard and will
                  show you whatever a URL returns. The cloud metadata service stays unreachable at every
                  setting.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Form.Text muted className="d-block mb-3">
            Admins also get a dashboard notification for every alert, regardless of these settings.
          </Form.Text>

          <hr />
          <h6 className="text-muted">Timing</h6>
          <Row>
            <Col md={3}>
              {numField(
                'Interval (seconds)',
                'interval_seconds',
                'Default 3600. A site can run more or less often than this.',
              )}
            </Col>
            <Col md={3}>{numField('Probe timeout (ms)', 'probe_timeout_ms')}</Col>
            <Col md={3}>
              {numField(
                'Attempts per run',
                'attempts_per_run',
                'Retries happen inside one run. A check only alerts if every attempt failed, which is what keeps a dropped packet from paging anyone.',
              )}
            </Col>
            <Col md={3}>{numField('Retry delay (seconds)', 'retry_delay_seconds')}</Col>
          </Row>
          <Row>
            <Col md={3}>
              {numField(
                'Re-alert (hours)',
                'realert_hours',
                'How often a site that stays down repeats. It alerts once on the way down regardless.',
              )}
            </Col>
          </Row>

          <hr />
          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setForm(original);
                setMsg(null);
              }}
              disabled={!hasChanges || saving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
          <div className="mt-2 text-end" style={{ minHeight: '1.5rem' }}>
            {status && (
              <small className={status.className}>
                {status.text}
                {status.dismissable && (
                  <Button
                    variant="link"
                    size="sm"
                    className="py-0 ps-2 pe-0 align-baseline"
                    onClick={() => setMsg(null)}
                  >
                    dismiss
                  </Button>
                )}
              </small>
            )}
          </div>
        </Card.Body>
      </Card>

      <MonitoredSitesCard settings={form} />

      <Card className="mt-3">
        <Card.Header>
          <span>
            <i className="bi bi-play-circle me-2" />
            Run and test
          </span>
        </Card.Header>
        <Card.Body>
          {actionMsg && (
            <Alert
              variant={actionMsg.variant}
              dismissible
              onClose={() => setActionMsg(null)}
              className="mb-3"
            >
              {actionMsg.text}
            </Alert>
          )}

          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={runCheck}
              disabled={hasChanges || running !== null}
            >
              {running === 'check' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Checking…
                </>
              ) : (
                <>
                  <i className="bi bi-search me-1" />
                  Check all sites now
                </>
              )}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={sendTestAlert}
              disabled={hasChanges || running !== null}
            >
              {running === 'test' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Sending…
                </>
              ) : (
                <>
                  <i className="bi bi-send me-1" />
                  Send test alert
                </>
              )}
            </Button>
          </div>
          <Form.Text muted className="d-block mt-2">
            {hasChanges
              ? 'Save your settings first — both buttons run against what is stored, not what is on screen above.'
              : 'Checking runs the same code the schedule runs, and sends real alerts if a site has changed state. It never pings the dead-man’s switch, so a run here cannot hide a stopped scheduler.'}
          </Form.Text>

          {testStages && (
            <Alert
              variant={testStages.some((s) => !s.ok) ? 'danger' : 'success'}
              dismissible
              onClose={() => setTestStages(null)}
              className="mt-3"
            >
              <div className="fw-semibold">
                <i className="bi bi-flask me-2" />
                Test alert —{' '}
                {testStages.some((s) => !s.ok)
                  ? `${testStages.filter((s) => !s.ok).length} of ${testStages.length} channels failed`
                  : `all ${testStages.length} channels accepted it`}
              </div>
              <ul className="mb-0 mt-2 small list-unstyled">
                {testStages.map((s, i) => (
                  <li key={i}>
                    <i
                      className={`bi ${s.ok ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'} me-2`}
                    />
                    {s.name}
                    {s.detail ? <span className="text-muted"> — {s.detail}</span> : null}
                  </li>
                ))}
              </ul>
              <hr className="my-2" />
              <div className="small text-muted">
                A synthetic alert for <code>test.invalid</code>, not a real site. Accepted means the channel
                took it, not that anyone read it — confirm each one actually arrived. The dead-man&rsquo;s
                switch is only resolved in DNS here, never pinged: a ping would reset its timer and hide a
                stopped scheduler.
              </div>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

// --- sites ------------------------------------------------------------------

const blankCheck: SiteCheck = {
  url: '/',
  method: 'GET',
  expect_status: '200',
  enabled: true,
  follow_redirects: true,
  header_op: '',
  expect_header: '',
  expect_header_value: '',
  expect_body_contains: '',
  request_body: '',
  request_content_type: '',
};

// What counts as a change worth sending, for the save reconciliation below.
const sameCheck = (a: SiteCheck, b: SiteCheck): boolean =>
  JSON.stringify({
    ...a,
    last_checked_at: 0,
    last_ok: 0,
    last_status_code: 0,
    last_duration_ms: 0,
    last_attempts: 0,
    last_error: 0,
  }) ===
  JSON.stringify({
    ...b,
    last_checked_at: 0,
    last_ok: 0,
    last_status_code: 0,
    last_duration_ms: 0,
    last_attempts: 0,
    last_error: 0,
  });

const MonitoredSitesCard: React.FC<{ settings: WebMonSettings }> = ({ settings }) => {
  const { data: sites, isLoading, refetch } = useGetWebmonSites();
  const createMutation = usePostWebmonSites();
  const updateMutation = usePutWebmonSitesSiteId();
  const deleteMutation = useDeleteWebmonSitesSiteId();
  const checkMutation = usePostWebmonCheck();
  const createCheck = usePostWebmonSitesSiteIdChecks();
  const updateCheck = usePutWebmonSitesSiteIdChecksCheckId();
  const deleteCheck = useDeleteWebmonSitesSiteIdChecksCheckId();
  const tryCheck = usePostWebmonTryCheck();

  const [editing, setEditing] = useState<MonitoredSite | null>(null);
  const [originalChecks, setOriginalChecks] = useState<SiteCheck[]>([]);
  const [isNew, setIsNew] = useState(false);
  const [tab, setTab] = useState('site');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MonitoredSite | null>(null);

  // The check being edited, held apart from the list.
  //
  // A new check is not in `checks` until it is saved, so cancelling leaves
  // nothing behind — the same contract as adding a site. index is null for a
  // new one, otherwise the row it will replace.
  const [draft, setDraft] = useState<{ index: number | null; value: SiteCheck } | null>(null);

  // Row being dragged, for reordering. Native HTML5 drag, as DataTable already
  // uses for its columns — no new dependency for one list.
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [tried, setTried] = useState<SiteCheckTryResult | null>(null);
  const [trying, setTrying] = useState(false);

  // Which mode each override field is in, while the editor is open.
  //
  // Held rather than derived from the value, because the two cannot always be
  // recovered from it: when the inherited default is blank, "set but not yet
  // typed" and "explicitly none" are both the empty string, and a derived mode
  // would flip the field back to None the moment you chose Set.
  const [overrideModes, setOverrideModes] = useState<Record<string, string>>({});

  const rows = useMemo(() => sites ?? [], [sites]);
  const checks = editing?.checks ?? [];

  // What Save will actually store: the list, plus whatever is still open in
  // the check editor. Counting only the list is what made the footer say "no
  // checks" while a filled-in one was on screen.
  const draftPending = !!draft && String(draft.value.url ?? '').trim() !== '';
  const pendingCount = checks.length + (draftPending && draft?.index === null ? 1 : 0);

  const openEditor = (site: MonitoredSite, fresh: boolean) => {
    setEditing({ ...site, checks: [...(site.checks ?? [])] });
    setOriginalChecks([...(site.checks ?? [])]);
    setIsNew(fresh);
    // A new site starts on Site; an existing one opens on Checks, which is what
    // you are usually there to change.
    setTab(fresh ? 'site' : 'checks');
    setDraft(null);
    setTried(null);
    setOverrideModes({});
    setError(null);
  };

  const setChecks = (next: SiteCheck[]) => setEditing((e) => (e ? { ...e, checks: next } : e));

  const checkOne = async (site: MonitoredSite) => {
    setChecking(site.id!);
    setError(null);
    try {
      await checkMutation.mutateAsync({ data: { site_ids: [site.id!] } });
      await refetch();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to check ${site.name}`);
    } finally {
      setChecking(null);
    }
  };

  // One Save for the whole site, checks included.
  //
  // The checks are separate endpoints, so this reconciles them: create the ones
  // without an id, update the ones that changed, delete the ones that are gone.
  // Position is taken from the list order, so reordering needs no separate call.
  const save = async () => {
    if (!editing) return;

    // A check still open in the editor is part of what was typed, so Save
    // takes it. Discarding it is how a filled-in check becomes "no checks":
    // the editor has its own button, directly above this one, and missing it
    // looks exactly like the save silently failing.
    let pending = checks;
    if (draft) {
      if (!String(draft.value.url ?? '').trim()) {
        setTab('checks');
        setError('The check you are editing needs a URL. Fill it in, or cancel it, then save.');
        return;
      }
      pending =
        draft.index === null
          ? [...checks, draft.value]
          : checks.map((old, j) => (j === draft.index ? draft.value : old));
      setChecks(pending);
      setDraft(null);
      setTried(null);
    }

    setSaving(true);
    setError(null);
    try {
      let siteId = editing.id;
      if (isNew) {
        const created = await createMutation.mutateAsync({ data: { ...editing, checks: undefined } });
        siteId = created?.id;
      } else {
        await updateMutation.mutateAsync({ siteId: siteId!, data: { ...editing, checks: undefined } });
      }
      if (!siteId) throw new Error('the site was saved but returned no id');

      const kept = new Set(pending.map((c) => c.id).filter(Boolean));
      for (const old of originalChecks) {
        if (old.id && !kept.has(old.id)) {
          await deleteCheck.mutateAsync({ siteId, checkId: old.id });
        }
      }
      for (let i = 0; i < pending.length; i++) {
        const c = { ...pending[i], position: i };
        if (!c.id) {
          await createCheck.mutateAsync({ siteId, data: c });
          continue;
        }
        const before = originalChecks.find((o) => o.id === c.id);
        if (!before || !sameCheck(before, c)) {
          await updateCheck.mutateAsync({ siteId, checkId: c.id, data: c });
        }
      }

      await refetch();
      setEditing(null);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save site',
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (site: MonitoredSite) => {
    setError(null);
    try {
      await deleteMutation.mutateAsync({ siteId: site.id! });
      await refetch();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove site');
    } finally {
      setConfirmRemove(null);
    }
  };

  // Testing takes the check as it stands, so nothing has to be saved first.
  const runTry = async (c: SiteCheck) => {
    setTrying(true);
    setTried(null);
    setError(null);
    try {
      setTried(
        await tryCheck.mutateAsync({
          data: {
            base_url: editing?.base_url ?? '',
            // Only to pick up this site's probe-reach override, when it has one.
            site_id: editing?.id,
            check: c,
          },
        }),
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to run the check');
    } finally {
      setTrying(false);
    }
  };

  const overrideField = (
    label: string,
    value: string | null | undefined,
    inherited: string | undefined,
    onChange: (v: string | null) => void,
    help?: string,
  ) => {
    const derived = value == null ? 'inherit' : value === '' ? 'none' : 'set';
    const mode = overrideModes[label] ?? derived;
    const inheritedLabel = inherited && inherited.trim() !== '' ? inherited : 'nothing';

    return (
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <Form.Select
          className="mb-2"
          value={mode}
          onChange={(e) => {
            setOverrideModes((m) => ({ ...m, [label]: e.target.value }));
            switch (e.target.value) {
              case 'inherit':
                onChange(null);
                break;
              case 'none':
                onChange('');
                break;
              default:
                onChange(value ?? '');
            }
          }}
        >
          <option value="inherit">Inherit — {inheritedLabel}</option>
          <option value="set">Set for this site</option>
          <option value="none">None for this site</option>
        </Form.Select>
        {mode === 'set' && (
          <Form.Control type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
        )}
        {help && <Form.Text muted>{help}</Form.Text>}
      </Form.Group>
    );
  };

  const overrideNum = (
    label: string,
    value: number | null | undefined,
    inherited: number | undefined,
    onChange: (v: number | null) => void,
  ) => (
    <Form.Group className="mb-3">
      <Form.Label className="d-flex justify-content-between align-items-center">
        <span>{label}</span>
        {value != null && (
          <Button variant="link" size="sm" className="py-0 px-0" onClick={() => onChange(null)}>
            use default
          </Button>
        )}
      </Form.Label>
      <Form.Control
        type="number"
        value={value ?? ''}
        placeholder={inherited != null ? `${inherited} (inherited)` : 'inherited'}
        onChange={(e) => onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
      />
    </Form.Group>
  );

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>
          <i className="bi bi-hdd-network me-2" />
          Monitored sites
        </span>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => openEditor({ name: '', base_url: '', enabled: true, checks: [] }, true)}
        >
          <i className="bi bi-plus-lg me-1" />
          Add site
        </Button>
      </Card.Header>
      <Card.Body>
        {error && !editing && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <Table hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Site</th>
                <th>Status</th>
                <th>Checks</th>
                <th>Last checked</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted">
                    No sites yet.
                  </td>
                </tr>
              )}
              {rows.map((site) => {
                const badge = siteBadge(site);
                const n = site.checks?.length ?? 0;
                return (
                  <tr key={site.id}>
                    <td>
                      {site.name}
                      {!site.enabled && (
                        <Badge bg="secondary" className="ms-2">
                          paused
                        </Badge>
                      )}
                      <div className="small text-muted">{site.base_url}</div>
                    </td>
                    <td>
                      <Badge bg={badge.variant}>{badge.label}</Badge>
                      {site.last_error && <div className="small text-danger">{site.last_error}</div>}
                    </td>
                    <td>{n === 0 ? <span className="text-warning">none</span> : n}</td>
                    <td>{formatWhen(site.last_checked_at)}</td>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => checkOne(site)}
                        disabled={checking !== null}
                      >
                        {checking === site.id ? <Spinner animation="border" size="sm" /> : 'Check now'}
                      </Button>
                      <Button variant="link" size="sm" onClick={() => openEditor(site, false)}>
                        Edit
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => setConfirmRemove(site)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        <Form.Text muted className="d-block">
          This monitor runs inside the dashboard, so it cannot report the dashboard itself being down — only
          the dead-man&rsquo;s switch can. Watching the dashboard here still catches a broken route or a bad
          deploy, which is worth having.
        </Form.Text>
      </Card.Body>

      <Modal show={!!confirmRemove} onHide={() => setConfirmRemove(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove {confirmRemove?.name}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This site and all of its checks stop being monitored, and what has been learned about it — when it
            last passed and what it has already alerted on — is cleared.
          </p>
          <p className="mb-0 text-muted small">
            You can add {confirmRemove?.name} again later, but it comes back as a new site with no checks and
            no history.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
            Keep monitoring it
          </Button>
          <Button variant="danger" onClick={() => confirmRemove && remove(confirmRemove)}>
            Remove site
          </Button>
        </Modal.Footer>
      </Modal>

      {editing && (
        <Modal show onHide={() => setEditing(null)} size="lg" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>{isNew ? 'Add site' : `Edit ${editing.name || 'site'}`}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Tabs activeKey={tab} onSelect={(k) => setTab(k ?? 'site')} className="mb-3">
              <Tab eventKey="site" title="Site">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={editing.name ?? ''}
                        placeholder="www.arrayscape.com"
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Base URL</Form.Label>
                      <Form.Control
                        type="text"
                        value={editing.base_url ?? ''}
                        placeholder="https://www.arrayscape.com"
                        onChange={(e) => setEditing({ ...editing, base_url: e.target.value })}
                      />
                      <Form.Text muted>What a check&rsquo;s relative URL is resolved against.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Check
                  type="switch"
                  id="site-enabled"
                  label="Check this site on the schedule"
                  checked={editing.enabled !== false}
                  onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                />
              </Tab>

              <Tab eventKey="checks" title={`Checks (${pendingCount})`}>
                {/* The editor sits above the list rather than replacing it, so
                the rows visibly accumulate as checks are added. Swapping the
                list out made each new check look like it had gone nowhere. */}
                {draft !== null && (
                  <div className="mb-3">
                    <CheckEditor
                      check={draft.value}
                      isNew={draft.index === null}
                      baseUrl={editing.base_url ?? ''}
                      onChange={(v) => setDraft({ ...draft, value: v })}
                      onCancel={() => {
                        setDraft(null);
                        setTried(null);
                      }}
                      onSave={() => {
                        // Appends at the bottom, so a new check never silently
                        // changes which existing failure names the alert.
                        setChecks(
                          draft.index === null
                            ? [...checks, draft.value]
                            : checks.map((old, j) => (j === draft.index ? draft.value : old)),
                        );
                        setDraft(null);
                        setTried(null);
                      }}
                      onTest={() => runTry(draft.value)}
                      trying={trying}
                      tried={tried}
                      canTest={!!String(draft.value.url ?? '').trim()}
                    />
                  </div>
                )}

                {draft === null && (
                  <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                    <Form.Text muted className="mb-0">
                      Checks run in order, and any one failing takes the site down. The first failure names
                      the alert, so put &ldquo;is it even up&rdquo; first. Drag a row to reorder.
                    </Form.Text>
                    {/* flex-shrink-0 and text-nowrap together: without them the
                    paragraph squeezes the button until its label wraps. */}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="text-nowrap flex-shrink-0"
                      onClick={() => {
                        setDraft({ index: null, value: { ...blankCheck } });
                        setTried(null);
                      }}
                    >
                      <i className="bi bi-plus-lg me-1" />
                      Add check
                    </Button>
                  </div>
                )}

                {draft !== null && checks.length > 0 && (
                  <div className="small text-muted mb-1">
                    Added so far — these are stored when you save the site:
                  </div>
                )}

                {checks.length === 0 && draft === null && (
                  <Alert variant="warning" className="py-2">
                    No checks yet. A site with none is never reported healthy — it is simply never asked. Add
                    at least one.
                  </Alert>
                )}

                <Table size="sm" className="align-middle">
                  <tbody>
                    {checks.map((c, i) => {
                      const badge = checkBadge(c);
                      return (
                        <tr
                          key={c.id ?? `new-${i}`}
                          draggable
                          onDragStart={() => setDragFrom(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (dragFrom === null || dragFrom === i) return;
                            const next = [...checks];
                            const [moved] = next.splice(dragFrom, 1);
                            next.splice(i, 0, moved);
                            setChecks(next);
                            setDragFrom(null);
                          }}
                          onDragEnd={() => setDragFrom(null)}
                          className={dragFrom === i ? 'opacity-50' : ''}
                          style={{ cursor: 'grab' }}
                        >
                          <td style={{ width: 24 }} className="text-muted">
                            <i className="bi bi-grip-vertical" />
                          </td>
                          <td style={{ width: 90 }}>
                            <Badge bg={badge.variant}>{badge.label}</Badge>
                            {!c.enabled && (
                              <Badge bg="secondary" className="ms-1">
                                off
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div className="small fw-semibold">{checkTypeOf(c).name}</div>
                            <code className="small">{describeCheck(c)}</code>
                            {c.last_error && <div className="small text-danger">{c.last_error}</div>}
                          </td>
                          <td style={{ width: 150 }} className="text-end">
                            {/* Kept alongside dragging: a keyboard has no
                                drag gesture, and reordering is how you
                                choose which failure names the alert. */}
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              disabled={i === 0}
                              aria-label="Move up"
                              onClick={() => {
                                const next = [...checks];
                                [next[i - 1], next[i]] = [next[i], next[i - 1]];
                                setChecks(next);
                              }}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              disabled={i === checks.length - 1}
                              aria-label="Move down"
                              onClick={() => {
                                const next = [...checks];
                                [next[i], next[i + 1]] = [next[i + 1], next[i]];
                                setChecks(next);
                              }}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-2"
                              onClick={() => {
                                setDraft({ index: i, value: { ...c } });
                                setTried(null);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => setChecks(checks.filter((_, j) => j !== i))}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Tab>

              <Tab eventKey="overrides" title="Overrides">
                <Form.Text muted className="d-block mb-3">
                  Leave everything inheriting unless this site genuinely differs. Changing a default in the
                  settings above then reaches every site that inherits it.
                </Form.Text>
                <Row>
                  <Col md={4}>
                    {overrideNum(
                      'Interval (seconds)',
                      editing.interval_seconds,
                      settings.interval_seconds,
                      (v) => setEditing({ ...editing, interval_seconds: v }),
                    )}
                  </Col>
                  <Col md={4}>
                    {overrideNum(
                      'Probe timeout (ms)',
                      editing.probe_timeout_ms,
                      settings.probe_timeout_ms,
                      (v) => setEditing({ ...editing, probe_timeout_ms: v }),
                    )}
                  </Col>
                  <Col md={4}>
                    {overrideNum('Re-alert (hours)', editing.realert_hours, settings.realert_hours, (v) =>
                      setEditing({ ...editing, realert_hours: v }),
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    {overrideNum(
                      'Attempts per run',
                      editing.attempts_per_run,
                      settings.attempts_per_run,
                      (v) => setEditing({ ...editing, attempts_per_run: v }),
                    )}
                  </Col>
                  <Col md={4}>
                    {overrideNum(
                      'Retry delay (seconds)',
                      editing.retry_delay_seconds,
                      settings.retry_delay_seconds,
                      (v) => setEditing({ ...editing, retry_delay_seconds: v }),
                    )}
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between align-items-center">
                        <span>Probe reach</span>
                        {editing.target_policy != null && (
                          <Button
                            variant="link"
                            size="sm"
                            className="py-0 px-0"
                            onClick={() => setEditing({ ...editing, target_policy: null })}
                          >
                            use default
                          </Button>
                        )}
                      </Form.Label>
                      <Form.Select
                        value={editing.target_policy ?? ''}
                        onChange={(e) =>
                          setEditing({
                            ...editing,
                            target_policy: e.target.value === '' ? null : (e.target.value as any),
                          })
                        }
                      >
                        <option value="">Inherit — {targetPolicyLabel(settings.target_policy)}</option>
                        {Object.entries(TARGET_POLICY_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {overrideField(
                      'Slack webhook URL',
                      editing.slack_webhook_url,
                      settings.slack_webhook_url,
                      (v) => setEditing({ ...editing, slack_webhook_url: v }),
                    )}
                  </Col>
                  <Col md={6}>
                    {overrideField(
                      'Alert recipients',
                      editing.alert_recipients,
                      settings.alert_recipients,
                      (v) => setEditing({ ...editing, alert_recipients: v }),
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {overrideField('From address', editing.from_address, settings.from_address, (v) =>
                      setEditing({ ...editing, from_address: v }),
                    )}
                  </Col>
                  <Col md={6}>
                    {overrideField('From name', editing.from_name, settings.from_name, (v) =>
                      setEditing({ ...editing, from_name: v }),
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    {overrideField('User agent', editing.user_agent, settings.user_agent, (v) =>
                      setEditing({ ...editing, user_agent: v }),
                    )}
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <span className="small text-muted">
              {pendingCount === 0
                ? 'This site has no checks, so it will never be reported healthy.'
                : `Saves the site and its ${pendingCount} check${pendingCount === 1 ? '' : 's'}` +
                  (draftPending ? ', including the one open above.' : '.')}
            </span>
            <span>
              <Button variant="secondary" className="me-2" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={save}
                disabled={saving || !String(editing.name ?? '').trim()}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Saving…
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </span>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};

// --- one check --------------------------------------------------------------

// Editing a single rule, in place on the Checks tab rather than in a second
// modal stacked on the first.
const CheckEditor: React.FC<{
  check: SiteCheck;
  isNew: boolean;
  baseUrl: string;
  onChange: (c: SiteCheck) => void;
  onCancel: () => void;
  onSave: () => void;
  onTest: () => void;
  trying: boolean;
  tried: SiteCheckTryResult | null;
  canTest: boolean;
}> = ({ check, isNew, baseUrl, onChange, onCancel, onSave, onTest, trying, tried, canTest }) => {
  const set = (patch: Partial<SiteCheck>) => onChange({ ...check, ...patch });
  const type = checkTypeOf(check);

  // A bordered panel, so this reads as a form nested inside the site editor
  // rather than as more of the site's own fields. Unframed, its buttons and the
  // modal's own Cancel/Save looked like one set, and the wrong one got clicked.
  return (
    <Card className="border">
      <Card.Header className="fw-semibold py-2">
        {isNew ? 'Add check' : 'Edit check'}
        <span className="fw-normal text-muted small ms-2">
          Added to the list below. Nothing is stored until you save the site.
        </span>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Label>What to check</Form.Label>
          <Form.Select
            value={type.name}
            onChange={(e) => {
              const preset = PRESETS.find((p) => p.name === e.target.value);
              if (preset) onChange({ ...check, ...preset.apply(baseUrl) });
            }}
          >
            {PRESETS.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
              </option>
            ))}
          </Form.Select>
          <Form.Text muted>{type.describe}</Form.Text>
        </Form.Group>

        <hr />

        <Row>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Method</Form.Label>
              <Form.Select value={check.method ?? 'GET'} onChange={(e) => set({ method: e.target.value })}>
                <option>GET</option>
                <option>HEAD</option>
                <option>POST</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={9}>
            <Form.Group className="mb-3">
              <Form.Label>URL</Form.Label>
              <Form.Control
                type="text"
                value={check.url ?? ''}
                placeholder="/"
                onChange={(e) => set({ url: e.target.value })}
              />
              <Form.Text muted>
                Relative to the site&rsquo;s base URL, or absolute to reach a different host.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Expect status</Form.Label>
              <Form.Control
                type="text"
                value={check.expect_status ?? ''}
                placeholder="200"
                onChange={(e) => set({ expect_status: e.target.value })}
              />
              <Form.Text muted>One code, or several: 400,422</Form.Text>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Check
              type="switch"
              id="check-follow"
              className="mt-4"
              label="Follow redirects"
              checked={check.follow_redirects !== false}
              onChange={(e) => set({ follow_redirects: e.target.checked })}
            />
            <Form.Text muted>
              Turn this off to assert on a redirect itself — followed, it returns the target&rsquo;s 200 and
              the redirect is never seen.
            </Form.Text>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Body contains</Form.Label>
          <Form.Control
            type="text"
            value={check.expect_body_contains ?? ''}
            onChange={(e) => set({ expect_body_contains: e.target.value })}
          />
          <Form.Text muted>
            Plain text, not a pattern. Leave blank to skip. A string only the real page carries is what
            catches an error page served with 200.
          </Form.Text>
        </Form.Group>

        <hr />
        <h6 className="text-muted">Header assertion</h6>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Rule</Form.Label>
              <Form.Select value={check.header_op ?? ''} onChange={(e) => set({ header_op: e.target.value })}>
                <option value="">None</option>
                <option value="absent">Must be absent</option>
                <option value="equals">Must equal</option>
                <option value="prefix">Must start with</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Header</Form.Label>
              <Form.Control
                type="text"
                value={check.expect_header ?? ''}
                placeholder="X-Robots-Tag"
                disabled={!check.header_op}
                onChange={(e) => set({ expect_header: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Value</Form.Label>
              <Form.Control
                type="text"
                value={check.expect_header_value ?? ''}
                disabled={!check.header_op || check.header_op === 'absent'}
                onChange={(e) => set({ expect_header_value: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>

        {check.method === 'POST' && (
          <>
            <hr />
            <h6 className="text-muted">Request body</h6>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={check.request_body ?? ''}
                    onChange={(e) => set({ request_body: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Content type</Form.Label>
                  <Form.Control
                    type="text"
                    value={check.request_content_type ?? ''}
                    placeholder="application/json"
                    onChange={(e) => set({ request_content_type: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        <hr />
        <Row>
          <Col md={8}>
            <Form.Group className="mb-0">
              <Form.Label>Label</Form.Label>
              <Form.Control
                type="text"
                value={check.label ?? ''}
                placeholder="Optional — shown in the alert instead of the rule"
                onChange={(e) => set({ label: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Check
              type="switch"
              id="check-enabled"
              className="mt-4"
              label="Enabled"
              checked={check.enabled !== false}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
          </Col>
        </Row>

        <hr />
        {/* Its own row, not squeezed in beside the test button: side by side the
      pair wrapped onto two lines and ended up stacked against the modal's own
      Cancel and Save. */}
        <div className="d-flex justify-content-end gap-2 flex-shrink-0 text-nowrap">
          <Button variant="outline-secondary" size="sm" onClick={onCancel}>
            Discard this check
          </Button>
          <Button variant="primary" size="sm" onClick={onSave} disabled={!String(check.url ?? '').trim()}>
            <i className="bi bi-plus-lg me-1" />
            {isNew ? 'Add to list' : 'Update in list'}
          </Button>
        </div>

        <hr />
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            variant="outline-secondary"
            size="sm"
            className="flex-shrink-0 text-nowrap"
            onClick={onTest}
            disabled={!canTest || trying}
          >
            {trying ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Testing…
              </>
            ) : (
              'Test this check'
            )}
          </Button>
          <Form.Text muted className="mb-0">
            {canTest
              ? 'Runs it once and shows what came back. Persists nothing, alerts nothing.'
              : 'Enter a URL and this will run the check as it stands.'}
          </Form.Text>
        </div>

        {tried && (
          <Alert variant={tried.ok ? 'success' : 'danger'} className="mt-2">
            <div className="fw-semibold">
              {tried.ok ? 'Passed' : 'Failed'} — {tried.method} {tried.url}
              {tried.status_code != null && ` → ${tried.status_code}`}
              {tried.duration_ms != null && ` (${tried.duration_ms}ms)`}
            </div>
            {tried.request_error && <div className="small text-danger mt-1">{tried.request_error}</div>}
            {tried.failures && tried.failures.length > 0 && (
              <ul className="small mb-0 mt-2">
                {tried.failures.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            )}
            {tried.headers && (
              <details className="mt-2 small">
                <summary>Response headers</summary>
                <pre className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                  {Object.entries(tried.headers)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join('\n')}
                </pre>
              </details>
            )}
            {tried.body_prefix && (
              <details className="mt-1 small">
                <summary>Body</summary>
                <pre className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                  {tried.body_prefix}
                </pre>
              </details>
            )}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default WebMonTab;
